/* eslint-disable react/jsx-no-useless-fragment */
import { BoxComponent } from '../../../../../../base/box/Box';
import { TypographyComponent } from '../../../../../../base/typography/Typography';
import { memo } from 'react';
import { extractFontFamilyFromUrl, getScaleSize } from '../../../../../../utils/utils';
import { useNormalizedPixels } from '../../../../../../hooks/useResize';
import { Template } from '@zeus/shared';
import placeholderItem from '../../../../../../icons/Images/Menuboard/item-placeholder.png';

interface ItemProps {
  template: Template;
  index: number;
  scale?: number;
}

const PlaceholderItem = ({ template, scale = 1, index }: ItemProps) => {
  const { normalizePixels } = useNormalizedPixels();
  const isItemPriceEnabled = template?.itemPrice && template.itemPrice.isEnabled;
  const isDescription = template?.itemDescription?.isEnabled;
  const itemTextScale = template?.item?.textScale ?? 1;
  const fontFamily = extractFontFamilyFromUrl(template?.fontFamily);

  return (
    <>
      <BoxComponent
        key={index}
        sx={{
          backgroundColor: 'transparent',
          borderRadius: 0,
          textTransform: 'capitalize',
          flex: 1,
          minWidth: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          maxWidth: '25%',
        }}
      >
        {/* Image container - takes remaining space */}
        <BoxComponent
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            overflow: 'hidden',
          }}
        >
          <img
            src={placeholderItem as unknown as string}
            alt={`Placeholder item ${index + 1}`}
            style={{
              width: '90%',
              height: '100%',
              borderRadius: `${normalizePixels(12, 'height')}px`,
            }}
          />
        </BoxComponent>

        {/* Item details - fixed at bottom */}
        <BoxComponent sx={{ flexShrink: 0, mt: 1, padding: `0 ${normalizePixels(12, 'width')}px`, height: '30%' }}>
          <TypographyComponent
            sx={{
              ...style.itemName,
              color: template.item.itemName.color ? template.item.itemName.color : '#840577',
              minWidth: 0,
              fontSize: getScaleSize(normalizePixels(36 * itemTextScale, 'height'), scale),
              fontFamily: fontFamily,
              textAlign: 'center',
            }}
          >
            ITEM {index + 1}
          </TypographyComponent>

          {isItemPriceEnabled && (
            <BoxComponent
              sx={{
                position: 'absolute',
                top: normalizePixels(6, 'height'),
                right: normalizePixels(8, 'width'),
                backgroundColor: '#888D95',
                borderRadius: '999px',
                padding: `${normalizePixels(6, 'height')}px ${normalizePixels(18, 'width')}px`,
              }}
            >
              <TypographyComponent
                sx={{
                  ...style.itemPrice,
                  color: template?.item?.itemPrice?.color ? template?.item?.itemPrice?.color : '#fff',
                  fontSize: getScaleSize(normalizePixels(26 * itemTextScale, 'height'), scale),
                  fontFamily: fontFamily,
                }}
              >
                $ 0.00
              </TypographyComponent>
            </BoxComponent>
          )}

          {isDescription && (
            <TypographyComponent
              sx={{
                ...style.itemDesc,
                color: template.item?.itemDescription?.color ? template.item?.itemDescription?.color : '#888D95',
                fontSize: getScaleSize(normalizePixels(18 * itemTextScale, 'height'), scale),
                fontFamily: fontFamily,
                textAlign: 'center',
              }}
            >
              Description for item {index + 1}
            </TypographyComponent>
          )}
        </BoxComponent>
      </BoxComponent>
    </>
  );
};

export default memo(PlaceholderItem);

const style = {
  container: {
    alignSelf: 'start',
    height: 'min-content',
    overflow: 'hidden',
  },
  itemNameContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  itemName: {
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    '-webkit-line-clamp': '1',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemPrice: {
    textAlign: 'center',
    fontFamily: 'Manrope, sans-serif',
    lineHeight: 'normal',
    fontWeight: '600',
  },
  itemDesc: {
    fontWeight: '400',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
  },
};
